import { Drawer, Select, Switch } from 'antd'
import { CloseOutlined, DownOutlined } from '@ant-design/icons'
import { ScheduleFilters, EventStatus, UNASSIGNED_CREW_ID, eventTypeOptions } from './scheduleModel'
import { useSchedule } from './ScheduleContext'
import { projectTypeOptionsFor, VisitWorkareaStatuses } from '../../common/constants'
import useServiceTicketsEnabled from '../../hooks/useServiceTicketsEnabled'

type Props = {
  open: boolean
  onClose: () => void
  filters: ScheduleFilters
  onFiltersChange: (filters: ScheduleFilters) => void
}

const FiltersDrawer = ({ open, onClose, filters, onFiltersChange }: Props) => {
  const { crew, divisions, salesPersons } = useSchedule()
  const showServiceTickets = useServiceTicketsEnabled()
  const projectTypeOptions = projectTypeOptionsFor(showServiceTickets)
  const crewOptions = [
    { value: UNASSIGNED_CREW_ID, label: 'Unassigned' },
    ...(crew?.filter(c => !!c.userEmail).map(c => ({ value: c.id.toString(), label: c.name })) || [])
  ]
  const uniqueDivisionNames = [...new Set(divisions?.map(d => d.name) || [])].sort((a, b) => a.localeCompare(b))
  const divisionsOptions = uniqueDivisionNames.map(name => ({ value: name, label: name }))
  const selectedDivisionNames = [...new Set(
    filters.divisionIds
      .map(id => divisions?.find(d => d.id === id)?.name)
      .filter((name): name is string => name != null)
  )]
  const salesPersonsOptions = salesPersons?.map(s => ({ value: s.email, label: s.displayName }))

  return (
    <Drawer
      title={<span style={{ fontSize: '18px', fontWeight: 'bold' }}>Filters</span>}
      placement="right"
      onClose={onClose}
      open={open}
      width={400}
      closeIcon={false}
      extra={<CloseOutlined onClick={onClose} style={{ fontSize: '18px', cursor: 'pointer' }} />}
      styles={{
        header: {
          padding: '24px'
        },
        body: {
          padding: '0 24px 24px 24px',
          backgroundColor: '#f5f5f5'
        }
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

        <div>
          <div style={{
            marginTop: '8px',
            marginBottom: '8px',
            fontWeight: '500',
            fontSize: '16px',
            color: '#333'
          }}>
            Event Type
          </div>
          <Select
            style={{ width: '100%', height: '48px' }}
            suffixIcon={<DownOutlined />}
            options={eventTypeOptions}
            mode="multiple"
            maxTagCount={3}
            placeholder="Select Event Types"
            value={filters.eventTypes}
            onChange={(value) => onFiltersChange({ ...filters, eventTypes: value })}
          />
        </div>

        <div>
          <div style={{
            marginBottom: '8px',
            fontWeight: '500',
            fontSize: '16px',
            color: '#333'
          }}>
            Crew Member
          </div>
          <Select
            style={{ width: '100%', height: '48px' }}
            suffixIcon={<DownOutlined />}
            options={crewOptions}
            mode="multiple"
            maxTagCount={2}
            placeholder="Select Crew Members"
            value={filters.crewMemberIds}
            onChange={(value) => onFiltersChange({ ...filters, crewMemberIds: value })}
          />
        </div>

        <div>
          <div style={{
            marginBottom: '8px',
            fontWeight: '500',
            fontSize: '16px',
            color: '#333'
          }}>
            Job Type
          </div>
          <Select
            style={{ width: '100%', height: '48px' }}
            suffixIcon={<DownOutlined />}
            options={projectTypeOptions}
            mode="multiple"
            maxTagCount={2}
            placeholder="Select Job Types"
            value={filters.projectTypeIds}
            onChange={(value) => onFiltersChange({ ...filters, projectTypeIds: value })}
          />
        </div>

        <div>
          <div style={{
            marginBottom: '8px',
            fontWeight: '500',
            fontSize: '16px',
            color: '#333'
          }}>
            Event Status
          </div>
          <Select
            style={{ width: '100%', height: '48px' }}
            suffixIcon={<DownOutlined />}
            options={[
              { value: EventStatus.Planned, label: 'Planned' },
              { value: EventStatus.ClockedIn, label: 'Clocked-In' },
              { value: EventStatus.Visited, label: 'Visited' }
            ]}
            placeholder="Select Event Statuses"
            mode="multiple"
            maxTagCount={2}
            value={filters.eventStatuses}
            onChange={(value) => onFiltersChange({ ...filters, eventStatuses: value })}
          />
        </div>

        <div>
          <div style={{
            marginBottom: '8px',
            fontWeight: '500',
            fontSize: '16px',
            color: '#333'
          }}>
            Visit Status
          </div>
          <Select
            style={{ width: '100%', height: '48px' }}
            suffixIcon={<DownOutlined />}
            options={[
              { value: VisitWorkareaStatuses.InProgress, label: 'In Progress' },
              { value: VisitWorkareaStatuses.Skipped, label: 'Skipped' },
              { value: VisitWorkareaStatuses.SkippedReschedule, label: 'Skipped (Reschedule)' },
              { value: VisitWorkareaStatuses.Completed, label: 'Completed' },
              { value: VisitWorkareaStatuses.CompletedUnbillable, label: 'Completed (Unbillable)' }
            ]}
            placeholder="Select Visit Statuses"
            mode="multiple"
            maxTagCount={2}
            value={filters.visitStatuses}
            onChange={(value) => onFiltersChange({ ...filters, visitStatuses: value })}
          />
        </div>

        <div>
          <div style={{
            marginBottom: '8px',
            fontWeight: '500',
            fontSize: '16px',
            color: '#333'
          }}>
            Division
          </div>
          <Select
            mode="multiple"
            maxTagCount={2}
            style={{ width: '100%', height: '48px' }}
            options={divisionsOptions}
            placeholder="Select Divisions"
            value={selectedDivisionNames}
            onChange={(names: string[]) => {
              const ids = divisions?.filter(d => names.includes(d.name)).map(d => d.id) || []
              onFiltersChange({ ...filters, divisionIds: ids })
            }}
          />
        </div>

        <div>
          <div style={{
            marginBottom: '8px',
            fontWeight: '500',
            fontSize: '16px',
            color: '#333'
          }}>
            Sales Person
          </div>
          <Select
            style={{ width: '100%', height: '48px' }}
            suffixIcon={<DownOutlined />}
            options={salesPersonsOptions}
            placeholder="Select Sales Persons"
            mode="multiple"
            maxTagCount={2}
            value={filters.salesPersonIds}
            onChange={(value) => onFiltersChange({ ...filters, salesPersonIds: value })}
          />
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginTop: '8px'
        }}>
          <Switch checked={filters.missingVisits} onChange={(value) => onFiltersChange({ ...filters, missingVisits: value })} />
          <span style={{ fontSize: '16px', color: '#333' }}>
            Only include jobs with no visits
          </span>
        </div>

      </div>
    </Drawer>
  )
}

export default FiltersDrawer
